import React, { useState, useEffect, useRef } from "react";

import "../components/MamangamHallBooking.css";
import { api } from "../config/api";
import HallCard from "./HallCard";
import HallBookingHero from "./HallBooking";

const PAGE_KEY = "mamangam-hall-booking";

const MamangamHallBooking = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const carouselRef = useRef(null);
  const carouselInitialized = useRef(false);

  useEffect(() => {
    loadPageData();
  }, []);

  const loadPageData = async () => {
    try {
      setLoading(true);
      const data = await api.getPageConfigByKey(PAGE_KEY);

      // Transform API data to match the expected structure
      const transformedData = {
        hero: {
          title: data.title || "Mamangam Hall Booking",
          subtitle: data.subtitle || "Mamangam Hall",
          description:
            data.description ||
            "Mamangam Hall at MY Cinemas is a premium multi-purpose space.",
        },
        sections: (data.sections || [])
          .filter((section) => section.is_active)
          .map((section) => ({
            id: section.id,
            slug: section.slug,
            title: section.title,
            imageUrl: section.data?.card_image_url || "",
            detailImageUrl: section.data?.detail_image_url || "",
            iconSvg: section.data?.icon_svg || "",
            featurePoints: section.data?.feature_points || [],
            whatsappNumber: section.data?.whatsapp_number || "",
            whatsappPrefilledText: section.data?.whatsapp_prefilled_text || "",
            order: section.display_order,
          })),
      };

      setPageData(transformedData);
    } catch (error) {
      console.error("Error loading Mamangam Hall Booking page:", error);
      // Fallback to empty data
      setPageData({
        hero: {
          title: "Mamangam Hall Booking",
          subtitle: "Mamangam Hall",
          description:
            "Mamangam Hall at MY Cinemas is a premium multi-purpose space.",
        },
        sections: [],
      });
    } finally {
      setLoading(false);
    }
  };

  // Initialize Owl Carousel after data loads
  useEffect(() => {
    if (
      loading ||
      !pageData ||
      pageData.sections.length === 0 ||
      !carouselRef.current
    ) {
      return;
    }

    // Wait for jQuery and Owl Carousel to be available
    const initCarousel = () => {
      if (!window.$ || !window.$.fn.owlCarousel) {
        setTimeout(initCarousel, 100);
        return;
      }

      const $carousel = window.$(carouselRef.current);

      // Destroy existing instance if any
      if ($carousel.hasClass("owl-loaded")) {
        try {
          $carousel.trigger("destroy.owl.carousel");
        } catch (e) {
          // ignore
        }
        $carousel.removeClass("owl-loaded owl-carousel");
      }

      // Initialize Owl Carousel
      $carousel.addClass("owl-carousel").owlCarousel({
        loop: false,
        margin: 20,
        nav: true,
        dots: true,
        autoplay: false,
        smartSpeed: 600,
        responsive: {
          0: {
            items: 1,
          },
          576: {
            items: 2,
          },
          768: {
            items: 3,
          },
          992: {
            items: 4,
          },
          1200: {
            items: 5,
          },
        },
      });

      carouselInitialized.current = true;
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(initCarousel, 100);

    return () => {
      clearTimeout(timer);
      if (carouselRef.current && window.$ && window.$.fn.owlCarousel) {
        const $carousel = window.$(carouselRef.current);
        if ($carousel.hasClass("owl-loaded")) {
          try {
            $carousel.trigger("destroy.owl.carousel");
          } catch (e) {
            // ignore
          }
        }
      }
      carouselInitialized.current = false;
    };
  }, [loading, pageData]);

  if (loading) {
    return (
      <div className="hall-section-loading">
        <div className="container py-5 text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      </div>
    );
  }

  const { hero, sections } = pageData;

  // Sort by `order` so admin reordering is respected
  const sortedSections = [...sections].sort((a, b) => a.order - b.order);

  return (
    <div className="hall-page">
      {/* ── Inner banner / hero ─────────────────────────────────────────── */}
      <HallBookingHero hero={hero} />

      {/* ── Card slider (Owl Carousel) ─────────────────────────────────── */}
      <div className="hall-section">
        <div className="container">
          {sortedSections.length > 0 ? (
            <div ref={carouselRef} className="hall-cards-carousel">
              {sortedSections.map((card) => (
                <HallCard key={card.id} card={card} />
              ))}
            </div>
          ) : (
            <div className="text-center py-5">
              <p>No categories available at the moment.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MamangamHallBooking;
